if (!customElements.get('paw-stable-banner-slider')) {
  customElements.define('paw-stable-banner-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const track = this.querySelector('[data-slim-track]');
      const slides = track ? [...track.querySelectorAll('[data-slim-slide]')] : [];
      if (slides.length < 3) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      const count = slides.length;
      const status = this.querySelector('[data-slim-status]');
      const controls = this.querySelector('[data-slim-controls]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let position = 0;
      let destination = 0;
      let pitch = 1;
      let direction = 1;
      let frame = 0;
      let wheelTimer;
      let wheelOrigin = null;
      let pointer = null;
      let suppressClickUntil = 0;
      const modulo = value => ((value % count) + count) % count;
      const render = () => {
        slides.forEach((slide, index) => {
          const offset = modulo(index - position + count / 2) - count / 2;
          slide.style.transform = `translate3d(${(offset * pitch * direction).toFixed(3)}px,0,0)`;
        });
      };
      const update = announce => {
        const current = modulo(Math.round(position));
        track.dataset.slimIndex = String(current);
        if (announce && status) status.textContent = slides[current].getAttribute('aria-label');
      };
      const stop = () => {
        cancelAnimationFrame(frame);
        frame = 0;
      };
      const go = (next, animate = true, announce = true) => {
        stop();
        clearTimeout(wheelTimer);
        wheelOrigin = null;
        destination = next;
        const start = position;
        if (!animate || reducedMotion.matches || Math.abs(next - start) < .001) {
          position = destination = modulo(next);
          render();
          update(announce);
          return;
        }
        const started = performance.now();
        const step = now => {
          const elapsed = Math.min(1, (now - started) / 320);
          position = start + (next - start) * (1 - Math.pow(1 - elapsed, 3));
          render();
          if (elapsed < 1) frame = requestAnimationFrame(step);
          else {
            frame = 0;
            position = destination = modulo(next);
            render();
            update(announce);
          }
        };
        frame = requestAnimationFrame(step);
      };
      const select = (index, animate = true) => {
        const current = Math.round(position);
        const distance = modulo(index - modulo(current) + count / 2) - count / 2;
        go(current + distance, animate);
      };
      const settleGesture = origin => {
        let next = Math.round(position);
        const distance = position - origin;
        if (next === Math.round(origin) && Math.abs(distance) * pitch > Math.min(40, pitch * .12)) next += Math.sign(distance);
        go(next);
      };
      track.setAttribute('data-slim-loop', '');
      const measure = () => {
        const style = getComputedStyle(track);
        direction = style.direction === 'rtl' ? -1 : 1;
        pitch = slides[0].getBoundingClientRect().width + (parseFloat(style.columnGap) || 0);
        render();
      };
      measure();
      update(false);
      if (controls) controls.hidden = false;
      this.querySelector('[data-slim-prev]')?.addEventListener('click', () => go((frame ? destination : Math.round(position)) - 1), {signal});
      this.querySelector('[data-slim-next]')?.addEventListener('click', () => go((frame ? destination : Math.round(position)) + 1), {signal});
      track.addEventListener('keydown', event => {
        if (event.target !== track) return;
        const current = frame ? destination : Math.round(position);
        if (event.key === 'ArrowLeft') go(current - direction);
        else if (event.key === 'ArrowRight') go(current + direction);
        else if (event.key === 'Home') select(0);
        else if (event.key === 'End') select(count - 1);
        else return;
        event.preventDefault();
      }, {signal});
      track.addEventListener('focusin', event => {
        if (pointer) return;
        const index = slides.indexOf(event.target.closest('[data-slim-slide]'));
        if (index >= 0) select(index, false);
      }, {signal});
      this.addEventListener('shopify:block:select', event => {
        const index = slides.indexOf(event.target.closest('[data-slim-slide]'));
        if (index >= 0) select(index, false);
      }, {signal});
      track.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        stop();
        clearTimeout(wheelTimer);
        wheelOrigin = null;
        pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, origin: position, dragging: false};
      }, {signal, passive: true});
      track.addEventListener('pointermove', event => {
        if (!pointer || event.pointerId !== pointer.id) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        if (!pointer.dragging) {
          if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
          pointer.dragging = true;
          track.setPointerCapture(event.pointerId);
        }
        position = pointer.origin - dx / pitch * direction;
        render();
      }, {signal, passive: true});
      const release = event => {
        if (!pointer || event.pointerId !== pointer.id) return;
        const finished = pointer;
        pointer = null;
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        if (finished.dragging) suppressClickUntil = performance.now() + 400;
        if (finished.dragging || Math.abs(position - Math.round(position)) > .001) settleGesture(finished.origin);
      };
      track.addEventListener('pointerup', release, {signal});
      track.addEventListener('pointercancel', release, {signal});
      track.addEventListener('lostpointercapture', release, {signal});
      track.addEventListener('pointerleave', event => {
        if (pointer && !pointer.dragging) release(event);
      }, {signal});
      track.addEventListener('click', event => {
        if (performance.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, {signal, capture: true});
      track.addEventListener('dragstart', event => event.preventDefault(), {signal});
      track.addEventListener('wheel', event => {
        if (event.ctrlKey || pointer || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        stop();
        if (wheelOrigin === null) wheelOrigin = position;
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? track.clientWidth : 1;
        position += event.deltaX * unit / pitch * direction;
        render();
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => settleGesture(wheelOrigin), 150);
      }, {signal, passive: false});
      this.observer = new ResizeObserver(measure);
      this.observer.observe(track);
      this.cleanup = () => {
        stop();
        clearTimeout(wheelTimer);
        track.removeAttribute('data-slim-loop');
        delete track.dataset.slimIndex;
        slides.forEach(slide => slide.style.removeProperty('transform'));
        if (controls) controls.hidden = true;
      };
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.cleanup?.();
      this.controller = null;
    }
  });
}
